import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { OFFICES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { full_name: "", phone: "", email: "", message: "", recaptcha_checked: false };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required.";
    if (!form.phone.trim()) e.phone = "Phone is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.recaptcha_checked) e.recaptcha = "Please confirm you are not a robot.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks, your message has been sent.", {
        description: "Our team will be in touch shortly.",
      });
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error("Submission failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const inputClass =
    "w-full bg-white border border-slate-300 px-4 py-3.5 text-base text-slate-900 placeholder-slate-400 transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="Connect with the ZT Health team"
        subtitle="Contact our team to learn more about the private investment services we offer to our nationwide network of investors."
      />

      <section className="zt-section">
        <div className="zt-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT: Form (primary) */}
          <div className="lg:col-span-7 order-1" data-testid="contact-form-section">
            <div className="p-8 md:p-10 border border-slate-200 bg-white shadow-[0_4px_24px_rgba(15,23,42,0.04)]">
              <p className="zt-overline mb-3">Send a Message</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-8 leading-tight">
                Tell us how we can help
              </h2>

              <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-semibold mb-2 text-slate-700">
                      Full Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      id="full_name"
                      type="text"
                      data-testid="contact-input-name"
                      value={form.full_name}
                      onChange={onChange("full_name")}
                      className={inputClass}
                      placeholder="Jane Doe"
                    />
                    {errors.full_name && <p className="text-red-600 text-sm mt-2" data-testid="error-name">{errors.full_name}</p>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold mb-2 text-slate-700">
                        Phone <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={onChange("phone")}
                        className={inputClass}
                        placeholder="(555) 555-5555"
                      />
                      {errors.phone && <p className="text-red-600 text-sm mt-2" data-testid="error-phone">{errors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold mb-2 text-slate-700">
                        Email <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={onChange("email")}
                        className={inputClass}
                        placeholder="jane@company.com"
                      />
                      {errors.email && <p className="text-red-600 text-sm mt-2" data-testid="error-email">{errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold mb-2 text-slate-700">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={onChange("message")}
                      className={`${inputClass} resize-none`}
                      placeholder="Tell us about your investment, partnership, or operating inquiry..."
                    />
                  </div>

                  {/* reCAPTCHA stub */}
                  <div data-testid="contact-recaptcha-wrapper">
                    <label className="inline-flex items-center gap-3 px-5 py-4 border border-slate-300 bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="contact-recaptcha"
                        checked={form.recaptcha_checked}
                        onChange={onChange("recaptcha_checked")}
                        className="w-5 h-5 accent-sky-500"
                      />
                      <span className="text-sm text-slate-700 font-medium">I'm not a robot</span>
                      <span className="ml-auto text-[10px] text-slate-400 tracking-wider uppercase">reCAPTCHA</span>
                    </label>
                    {errors.recaptcha && <p className="text-red-600 text-sm mt-2" data-testid="error-recaptcha">{errors.recaptcha}</p>}
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 text-sm font-semibold tracking-wide uppercase transition-all duration-200 disabled:opacity-60"
                    style={{ background: "var(--zt-teal)", color: "#fff" }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} /> Send
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* RIGHT: Office locations (secondary) */}
          <div className="lg:col-span-5 order-2" data-testid="contact-offices">
            <p className="zt-overline mb-6">Our Offices</p>
            <div className="space-y-10">
              {OFFICES.map((o) => (
                <div key={o.city} data-testid={`office-${o.city.toLowerCase().replace(/\s+/g, "-")}`} className="border-l-2 pl-6" style={{ borderColor: "var(--zt-border)" }}>
                  <h3 className="text-xl font-bold mb-2">{o.city}</h3>
                  <address className="not-italic text-slate-600 leading-relaxed">
                    {o.address1}<br />
                    {o.address2}
                  </address>
                  {(o.phone || o.fax) && (
                    <div className="mt-3 space-y-1 text-slate-700">
                      {o.phone && (
                        <p>
                          <span className="font-semibold mr-2">P:</span>
                          <a
                            href={`tel:${o.phone.replace(/[^0-9+]/g, "")}`}
                            className="hover:underline"
                            style={{ color: "var(--zt-navy)" }}
                            data-testid={`phone-${o.city.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {o.phone}
                          </a>
                        </p>
                      )}
                      {o.fax && (
                        <p className="text-slate-600">
                          <span className="font-semibold mr-2">F:</span> {o.fax}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
